/**
 * HERE Maps API Service via Supabase Edge Function
 * Resolve problema de CORS chamando via servidor
 */

import { supabase } from './supabase';

export interface HereDistanceResult {
  distanceKm: number;
  durationMins: number;
  durationNoTrafficMins?: number;
  routeSummary?: string | null;
  source?: 'here_routing_v8' | string;
  status: string;
}

export interface HereDistanceError {
  error: string;
  status?: string;
}

export interface HereGeocodingResult {
  lat: number;
  lng: number;
  address: string;
  confidence?: number;
}

export interface HereReverseGeocodingResult {
  address: string;
  confidence?: number;
  streetName?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  district?: string;
}

/**
 * Helper to get departure time in ISO 8601 format for HERE API
 * HERE doesn't accept "now" - it requires a valid ISO datetime
 */
const getHereDepartureTime = (departureTime?: string): string => {
  if (!departureTime || departureTime.trim().length === 0 || departureTime.toLowerCase() === "now") {
    return new Date().toISOString();
  }
  // Validate it's a valid date
  const date = new Date(departureTime);
  if (isNaN(date.getTime())) {
    return new Date().toISOString();
  }
  return date.toISOString();
};

/**
 * Calcula distância e tempo usando a Edge Function do Supabase
 * Evita CORS porque é chamada do servidor (Edge Function)
 * Suporta BYOK: passe store_id no objeto config para usar chaves do banco
 */
export const getHereMapsDistanceViaEdge = async (
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number,
  hereApiKeyOrConfig: string | { storeId?: string; hereMapsApiKey?: string },
  transportMode: 'car' | 'truck' = 'car',
  departureTime?: string
): Promise<HereDistanceResult | null> => {
  // Handle both old signature (apiKey string) and new (config object with storeId)
  let hereApiKey: string | undefined;
  let storeId: string | undefined;
  
  if (typeof hereApiKeyOrConfig === 'string') {
    hereApiKey = hereApiKeyOrConfig;
  } else if (hereApiKeyOrConfig) {
    storeId = hereApiKeyOrConfig.storeId;
    hereApiKey = hereApiKeyOrConfig.hereMapsApiKey;
  }
  
  // Convert departureTime to ISO format for HERE API
  const hereDepartureTime = getHereDepartureTime(departureTime);
  
  try {
    // Use BYOK Edge Function - prefers store_id, falls back to apiKey
    const { data, error } = await supabase.functions.invoke('here-maps-proxy', {
      body: {
        store_id: storeId, // BYOK: Edge Function will look up API key from database if provided
        apiKey: hereApiKey, // Legacy: used if store_id not available
        originLat,
        originLng,
        destLat,
        destLng,
        transportMode,
        departureTime: hereDepartureTime
      }
    });

    if (error) {
      console.error('[HereMaps] Edge Function Error:', error);
      throw new Error(`Edge Function Error: ${error.message || error}`);
    }

    if (data && typeof data.distanceKm === 'number' && typeof data.durationMins === 'number') {
      return {
        distanceKm: data.distanceKm,
        durationMins: data.durationMins,
        durationNoTrafficMins: data.durationNoTrafficMins,
        routeSummary: data.routeSummary,
        source: data.source,
        status: data.status || 'OK'
      };
    }

    console.warn('[HereMaps] Resposta inválida:', data);
    return null;
    
  } catch (e: any) {
    console.error('[HereMaps] Exception:', e.message);
    return null;
  }
};

/**
 * Wrapper para usar tanto via Edge Function (browser) quanto direto (server)
 * Detecta automaticamente o ambiente
 */
export const getHereMapsDistance = async (
  originLat: number,
  originLng: number,
  destLat: number,
  destLng: number,
  hereApiKey: string,
  useEdgeFunction: boolean = true,
  transportMode: 'car' | 'truck' = 'car',
  departureTime?: string
): Promise<HereDistanceResult | null> => {
  // Convert departureTime to ISO format for HERE API
  const hereDepartureTime = getHereDepartureTime(departureTime);
  
  // Se estamos no navegador, usar Edge Function (evita CORS)
  if (useEdgeFunction && typeof window !== 'undefined') {
    return getHereMapsDistanceViaEdge(originLat, originLng, destLat, destLng, hereApiKey, transportMode, hereDepartureTime);
  }
  
  // Se estamos no servidor (Edge Function), chamar direto
  // Nota: Este código só executa se chamado de outra Edge Function ou server-side
  const hereUrl = new URL('https://router.hereapi.com/v8/routes');
  hereUrl.searchParams.set('origin', `${originLat},${originLng}`);
  hereUrl.searchParams.set('destination', `${destLat},${destLng}`);
  hereUrl.searchParams.set('transportMode', transportMode);
  hereUrl.searchParams.set('departureTime', hereDepartureTime);
  hereUrl.searchParams.set('return', 'summary');
  hereUrl.searchParams.set('apiKey', hereApiKey);
  
  try {
    const response = await fetch(hereUrl.toString());
    const data = await response.json();
    
    // Verifica se há rotas válidas na resposta
    if (data.routes && Array.isArray(data.routes) && data.routes.length > 0) {
      const route = data.routes[0];
      
      if (route.sections && Array.isArray(route.sections) && route.sections.length > 0) {
        const section = route.sections[0];
        const summary = section.summary;
        
        if (summary && typeof summary.length === 'number' && typeof summary.duration === 'number') {
          const distanceMeters = summary.length;
          const durationSeconds = summary.duration;
          const baseDurationSeconds = summary.baseDuration ?? durationSeconds;
          
          return {
            distanceKm: parseFloat((distanceMeters / 1000).toFixed(2)),
            durationMins: Math.ceil(durationSeconds / 60),
            durationNoTrafficMins: Math.ceil(baseDurationSeconds / 60),
            routeSummary: null, // HERE v8 não fornece summary string como Google
            source: 'here_routing_v8',
            status: 'OK'
          };
        }
      }
    }
    
    console.warn('[HereMaps] Resposta inválida da API:', data);
    return null;
  } catch (e) {
    console.error('[HereMaps] Erro na chamada direta:', e);
    return null;
  }
};

/**
 * Geocodificação (endereço para coordenadas) via Edge Function
 * Converte endereço em latitude/longitude
 */
export const getHereGeocodingViaEdge = async (
  address: string,
  hereApiKey: string
): Promise<HereGeocodingResult | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('sal-here-maps-geocoding', {
      body: {
        address,
        apiKey: hereApiKey
      }
    });

    if (error) {
      console.error('[HereMaps] Geocoding Edge Function Error:', error);
      throw new Error(`Edge Function Error: ${error.message || error}`);
    }

    if (data && typeof data.lat === 'number' && typeof data.lng === 'number') {
      return {
        lat: data.lat,
        lng: data.lng,
        address: data.address,
        confidence: data.confidence
      };
    }

    console.warn('[HereMaps] Resposta de geocodificação inválida:', data);
    return null;
    
  } catch (e: any) {
    console.error('[HereMaps] Geocoding Exception:', e.message);
    return null;
  }
};

/**
 * Geocodificação reversa (coordenadas para endereço) via Edge Function
 * Converte latitude/longitude em endereço
 */
export const getHereReverseGeocodingViaEdge = async (
  lat: number,
  lng: number,
  hereApiKey: string
): Promise<HereReverseGeocodingResult | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('sal-here-maps-reverse-geocoding', {
      body: {
        lat,
        lng,
        apiKey: hereApiKey
      }
    });

    if (error) {
      console.error('[HereMaps] Reverse Geocoding Edge Function Error:', error);
      throw new Error(`Edge Function Error: ${error.message || error}`);
    }

    if (data && data.address) {
      return {
        address: data.address,
        confidence: data.confidence,
        streetName: data.streetName,
        houseNumber: data.houseNumber,
        postalCode: data.postalCode,
        city: data.city,
        district: data.district
      };
    }

    console.warn('[HereMaps] Resposta de geocodificação reversa inválida:', data);
    return null;
    
  } catch (e: any) {
    console.error('[HereMaps] Reverse Geocoding Exception:', e.message);
    return null;
  }
};
